import { z } from "zod"
import { assertSessionVersion, getSessionVersion } from "@/shared/lib/auth"
import { getLocale } from "@/shared/lib/i18n"
import { detectPlatform, detectStore, getDeviceId } from "@/shared/lib/platform"
import { notifyAccessDenied } from "../access-denied"
import { captureContractMismatch } from "../contract-mismatch"
import { AccessDeniedError, toHttpError } from "../errors"
import { networkAwareFetch } from "./fetch"
import { extractPathname, noteHttpFailure } from "./telemetry"
import { ensureTokenRefresh, resolveAccessToken } from "./tokens"

export const platform = detectPlatform()
export const store = detectStore() ?? "none"

async function resolveDeviceId(): Promise<string | null> {
	try {
		return await getDeviceId()
	} catch {
		return null
	}
}

// Fetch со всей обвязкой сгенерированного клиента (auth + refresh, локаль,
// платформа) для запросов мимо него — например SSE-стримов.
export async function authorizedFetch(
	input: RequestInfo | URL,
	init?: RequestInit,
): Promise<Response> {
	const version = getSessionVersion()
	const token = await resolveAccessToken()
	let sourceHeaders = init?.headers
	if (sourceHeaders === undefined && input instanceof Request) {
		sourceHeaders = input.headers
	}
	const headers = new Headers(sourceHeaders)
	if (token) headers.set("Authorization", `Bearer ${token}`)
	headers.set("Accept-Language", getLocale())
	headers.set("X-Platform", platform)
	headers.set("X-Store", store)
	const id = await resolveDeviceId()
	if (id) headers.set("X-Device-Id", id)
	assertSessionVersion(version)
	return networkAwareFetch(input, { ...init, headers })
}

const bufferedRequests = new WeakMap<Request, Request>()

// Clone request before body is consumed — enables retry after token refresh.
export async function prepareRequest(request: Request): Promise<Request> {
	const version = getSessionVersion()
	if (!request.headers.has("Accept-Language")) {
		request.headers.set("Accept-Language", getLocale())
	}
	if (!request.headers.has("X-Device-Id")) {
		const id = await resolveDeviceId()
		if (id) request.headers.set("X-Device-Id", id)
	}
	bufferedRequests.set(request, request.clone())
	assertSessionVersion(version)
	return request
}

const AUTH_ENTRY_PATHS = new Set([
	"/api/v1/auth/register",
	"/api/v1/auth/apple/login",
	"/api/v1/auth/google/login",
	"/api/v1/auth/email/login",
	"/api/v1/auth/email/login/send-code",
])

// Auth interceptor: refresh token on 401 (with mutex for concurrent requests)
export async function refreshOnUnauthorized(
	response: Response,
	request: Request,
	options: { url: string; fetch?: typeof fetch },
): Promise<Response> {
	if (response.status !== 401) return response
	if (AUTH_ENTRY_PATHS.has(options.url)) return response

	const buffered = bufferedRequests.get(request)
	if (!buffered) return response

	const tokens = await ensureTokenRefresh()

	const retryHeaders = new Headers(buffered.headers)
	retryHeaders.set("Authorization", `Bearer ${tokens.access_token}`)

	return (options.fetch ?? networkAwareFetch)(
		new Request(buffered, { headers: retryHeaders }),
	)
}

async function readErrorBody(response: Response): Promise<unknown> {
	// +json по RFC 6839: social-service отдаёт ошибки как application/problem+json.
	const [mediaType = ""] = (response.headers.get("content-type") ?? "").split(
		";",
	)
	const mime = mediaType.trim().toLowerCase()
	if (mime === "application/json" || mime.endsWith("+json")) {
		return response
			.clone()
			.json()
			.catch(() => undefined)
	}
	return response
		.clone()
		.text()
		.catch(() => undefined)
}

// Error interceptor: parse error responses
export async function throwOnErrorStatus(
	response: Response,
	request: Request,
): Promise<Response> {
	if (response.ok) return response

	const data = await readErrorBody(response)

	// path/method — иначе по событию в PostHog не видно, какой запрос упал.
	const path = extractPathname(request.url)
	noteHttpFailure(response.status, request.method, path)
	const error = toHttpError(response.status, data, {
		path,
		method: request.method,
	})
	if (error instanceof AccessDeniedError) notifyAccessDenied(error)
	throw error
}

export function reportContractMismatch(
	error: unknown,
	_response: Response | undefined,
	request: Request | undefined,
	options: { url: string; method?: string },
): unknown {
	if (!(error instanceof z.ZodError)) return error

	const method = request?.method ?? options.method ?? "GET"
	captureContractMismatch(
		"http.contract",
		`HTTP contract mismatch: ${method} ${options.url}`,
		error.issues,
		{ http_method: method, http_path: options.url },
	)
	return error
}
